import { JSX, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Rating, { IconContainerProps } from "@mui/material/Rating";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import StarBorderIcon from "@mui/icons-material/StarBorder";
import StarIcon from "@mui/icons-material/Star";

export type ScoreFieldProps = {
	value: string;
	disabled: boolean;

	minScore: number;
	maxScore: number;

	getScoreName: (score: number | null) => string; // function starts from minScore to maxScore
	getScoreInfo: (score: number | null) => string;
	getScoreColour: (score: number | null) => string;
	onChange: (value: string) => void;
};

const ScoreField: React.FC<ScoreFieldProps> = (props: ScoreFieldProps) => {
	const [hover, setHover] = useState(-1);

	function offsetScore(value: number): number {
		return value + props.minScore - 1;
	}

	function parseScore(): number | null {
		return props.value !== "" ? parseInt(props.value) : null;
	}

	function getScoreInfo(): string | null {
		const value = props.disabled ? parseScore() : hover;
		if (value === null || hover === -1) {
			return null;
		}

		return props.getScoreInfo(offsetScore(value));
	}

	function getScoreName(): string {
		const value = props.disabled || hover <= 0 ? parseScore() : hover;
		if (value === null) {
			return "";
		}

		return props.getScoreName(offsetScore(value));
	}

	// Every name a score can take, widest one included. See the sizer below.
	const scoreNames = useMemo((): string[] => {
		const names: string[] = [];
		for (
			let value = 1;
			value <= props.maxScore - props.minScore + 1;
			value++
		) {
			names.push(props.getScoreName(offsetScore(value)));
		}

		return names;
	}, [props.minScore, props.maxScore, props.getScoreName]);

	function iconContainer(containerProps: IconContainerProps): JSX.Element {
		const { value, ...other } = containerProps;

		const compare = props.disabled || hover <= 0 ? parseScore() : hover;
		let icon: JSX.Element;
		if (compare === null || value > compare) {
			icon = <StarBorderIcon />;
		} else {
			const colour = props.getScoreColour(offsetScore(compare));
			icon = <StarIcon sx={{ color: colour }} />;
		}

		return <span {...other}>{icon}</span>;
	}

	return (
		<>
			<Tooltip title={getScoreInfo() ?? ""} placement="right">
				<div
					style={{ display: "inline-block" }}
					onMouseEnter={() => setHover(-2)}
					onMouseLeave={() => setHover(-1)}>
					<Rating
						disabled={props.disabled}
						value={parseInt(props.value ?? "0")} // Value is a score starting from 1 up to max
						max={props.maxScore - props.minScore + 1}
						IconContainerComponent={iconContainer}
						onChange={(event, newValue): void => {
							setHover(-1);
							props.onChange(newValue?.toString() ?? "");
						}}
						onChangeActive={(event, newHover): void => {
							setHover(newHover);
						}}
					/>
				</div>
			</Tooltip>
			<Typography sx={{ minHeight: theme => theme.spacing(3) }}>
				{getScoreName()}
			</Typography>
			{/* Reserves the width of the widest name so the column cannot resize
			    when the hover swaps one name for another. Without it, competence
			    lists whose names are whole sentences re-wrap the surrounding field
			    row on hover, which moves the stars out from under the pointer,
			    which cancels the hover, which un-wraps the row: the score block
			    flickers for as long as the pointer rests on a star. */}
			<Box
				aria-hidden
				sx={{ height: 0, overflow: "hidden", visibility: "hidden" }}>
				{scoreNames.map((name, index) => (
					<Typography key={index}>{name}</Typography>
				))}
			</Box>
		</>
	);
};

export default ScoreField;
